import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import CalibrationForm from '../components/Calibrations/CalibrationForm';
import CertificateImportModal from '../components/Calibrations/CertificateImportModal';

// ─── Mapeamentos visuais ──────────────────────────────────────────────────────
const BADGE_TIPO = {
  externa: { rotulo: 'Externa', classe: 'bg-blue-900/50 text-blue-300 border border-blue-800/60' },
  interna: { rotulo: 'Interna', classe: 'bg-indigo-900/50 text-indigo-300 border border-indigo-800/60' },
};

const BADGE_RESULTADO = {
  aprovado: { rotulo: 'Aprovado', classe: 'bg-emerald-900/40 text-emerald-300 border border-emerald-800/50' },
  reprovado: { rotulo: 'Reprovado', classe: 'bg-red-900/40 text-red-300 border border-red-800/50' },
  aprovado_com_restricao: { rotulo: 'Ap. c/ Restrição', classe: 'bg-amber-900/40 text-amber-300 border border-amber-800/50' },
};

const BADGE_LAUDO = {
  rascunho:   { rotulo: 'Rascunho',   classe: 'bg-slate-800 text-slate-400 border border-slate-700' },
  em_revisao: { rotulo: 'Em Revisão', classe: 'bg-amber-900/40 text-amber-300 border border-amber-700' },
  concluido:  { rotulo: 'Concluído',  classe: 'bg-emerald-900/40 text-emerald-300 border border-emerald-700' },
};


// ─── Modal de rejeição ────────────────────────────────────────────────────────
function ModalRejeicao({ onConfirmar, onCancelar }) {
  const [motivo, setMotivo] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-md bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl shadow-2xl p-6">
        <h3 className="text-base font-semibold text-slate-100 mb-1">Rejeitar Laudo</h3>
        <p className="text-xs text-slate-500 mb-4">Informe o motivo para devolver o laudo ao rascunho.</p>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          placeholder="Ex: Dados de medição incompletos..."
          className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-600 text-sm resize-none focus:outline-none focus:border-blue-500"
        />
        <div className="flex gap-3 mt-4 justify-end">
          <button
            onClick={onCancelar}
            className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirmar(motivo)}
            disabled={!motivo.trim()}
            className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmar Rejeição
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Painel direito: dados inseridos pelo técnico ────────────────────────────
function PainelDadosLaudo({ calibracao }) {
  const { formatarData } = useTenant();
  const RESULTADO_LABEL = {
    aprovado: { text: 'Aprovado', cls: 'text-emerald-400 bg-emerald-900/30 border-emerald-800/50' },
    reprovado: { text: 'Reprovado', cls: 'text-red-400 bg-red-900/30 border-red-800/50' },
    aprovado_com_restricao: { text: 'Ap. c/ Restrição', cls: 'text-amber-400 bg-amber-900/30 border-amber-800/50' },
  };
  const res = RESULTADO_LABEL[calibracao.resultado] || null;

  function Campo({ label, value, mono }) {
    if (!value) return null;
    return (
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
        <p className={`text-xs text-slate-200 ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
    );
  }

  const padroesValidos = (calibracao.padroesUsados || []).filter(p => p.descricao || p.padraoReferencia?.tag);

  return (
    <div className="h-full overflow-y-auto px-5 py-4 space-y-5 text-slate-300">

      {/* Dados gerais */}
      <section className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Dados da Calibração</p>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Instrumento" value={calibracao.instrument?.tag} mono />
          <Campo label="Tipo" value={calibracao.calibrationType === 'externa' ? 'Calibração Externa' : 'Calibração Interna'} />
          <Campo label="Data" value={calibracao.data ? new Date(calibracao.data).toLocaleDateString('pt-BR') : null} />
          <Campo label="Próxima Calibração" value={calibracao.proximaData ? formatarData(calibracao.proximaData) : null} />
          <Campo label="Fornecedor / Lab." value={calibracao.fornecedor} />
          <Campo label="Acreditação" value={calibracao.acreditacaoFornecedor} />
          <Campo label="Nº Certificado" value={calibracao.numeroCertificado} mono />
          <Campo label="Procedimento" value={calibracao.procedimento ? `${calibracao.procedimento.numero} — ${calibracao.procedimento.descricao}` : null} />
        </div>
        {res && (
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${res.cls}`}>
            {res.text}
          </div>
        )}
        {calibracao.observacoes && (
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Observações</p>
            <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{calibracao.observacoes}</p>
          </div>
        )}
      </section>

      {/* Padrões de rastreabilidade */}
      {padroesValidos.length > 0 && (
        <section className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Padrões de Rastreabilidade</p>
          <div className="space-y-2">
            {padroesValidos.map((p, i) => (
              <div key={i} className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg p-3 grid grid-cols-2 gap-2">
                <Campo label="Descrição" value={p.descricao || p.padraoReferencia?.tag} />
                <Campo label="Nº Certificado" value={p.numeroCertificado} mono />
                <Campo label="Válido até" value={p.validoAte ? new Date(p.validoAte).toLocaleDateString('pt-BR') : null} />
                {p.incertezaExpandida != null && (
                  <Campo label="Incerteza (U)" value={`${p.incertezaExpandida} (k=${p.fatorK ?? 2})`} />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Grupos de medição */}
      {(calibracao.gruposMedicao || []).length > 0 && (
        <section className="space-y-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Grupos de Medição</p>
          {calibracao.gruposMedicao.map((g, gi) => (
            <div key={gi} className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-[var(--surface-4)] flex items-center justify-between">
                <span className="text-xs font-medium text-slate-200">{g.nome || `Grupo ${gi + 1}`}</span>
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  {g.unidade && <span>{g.unidade}</span>}
                  {g.empValor && <span>EMP: {g.empValor} {g.empTipo === 'percentual' ? '%' : g.unidade}</span>}
                </div>
              </div>
              {(g.pontos || []).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="text-slate-500 border-b border-[var(--surface-4)]">
                        <th className="px-3 py-1.5 text-left font-medium">Aplicado</th>
                        <th className="px-3 py-1.5 text-left font-medium">Lido</th>
                        <th className="px-3 py-1.5 text-left font-medium">Erro</th>
                        <th className="px-3 py-1.5 text-left font-medium">Incerteza</th>
                        <th className="px-3 py-1.5 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.pontos.map((p, pi) => (
                        <tr key={pi} className="border-b border-[var(--surface-4)]/50 last:border-0">
                          <td className="px-3 py-1.5 font-mono text-slate-300">{p.aplicado}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-300">{p.lido}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-400">{p.erro != null ? p.erro : '—'}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-400">{p.incerteza != null ? `${p.incerteza} (k=${p.fatorK ?? 2})` : '—'}</td>
                          <td className="px-3 py-1.5">
                            {p.aprovado === true && <span className="text-emerald-400">✓</span>}
                            {p.aprovado === false && <span className="text-red-400">✗</span>}
                            {p.aprovado == null && <span className="text-slate-600">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {calibracao.motivoRejeicao && (
        <section className="bg-red-900/20 border border-red-800/40 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-red-400 mb-1">Motivo de Rejeição Anterior</p>
          <p className="text-xs text-slate-300">{calibracao.motivoRejeicao}</p>
        </section>
      )}
    </div>
  );
}

// ─── Modal de visualização (revisor vê o PDF + dados antes de aprovar / detalhe read-only) ──
function ModalVisualizacao({ calibracao, onFechar, onAssinar, onRejeitar, onEditar, readOnly = false }) {
  const [mostrarRejeicao, setMostrarRejeicao] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [arquivoUrl, setArquivoUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [erroAssinar, setErroAssinar] = useState('');

  useEffect(() => {
    if (!calibracao.arquivoCertificado) return;
    api.get(`/calibrations/${calibracao._id}/certificado-url`)
      .then(r => setArquivoUrl(r.data.url))
      .catch(() => setPdfError(true));
  }, [calibracao._id, calibracao.arquivoCertificado]);

  const titulo = readOnly ? 'Detalhe do Laudo' : 'Revisão do Laudo';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[var(--surface-1)]">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between px-5 py-3 bg-[var(--surface-2)] border-b border-[var(--surface-4)] flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-100">{titulo}</span>
          <span className="font-mono text-xs bg-[var(--surface-4)] text-slate-300 px-2 py-0.5 rounded">
            {calibracao.instrument?.tag || '—'}
          </span>
          {calibracao.numeroCertificado && (
            <span className="text-xs text-slate-500">Nº {calibracao.numeroCertificado}</span>
          )}
          {calibracao.fornecedor && (
            <span className="text-xs text-slate-500">· {calibracao.fornecedor}</span>
          )}
          {readOnly && calibracao.assinadoPor && (
            <span className="text-xs text-emerald-500 bg-emerald-900/30 border border-emerald-800/40 px-2 py-0.5 rounded-full">
              ✓ Assinado por {calibracao.assinadoPor}
              {calibracao.assinadoEm && ` em ${new Date(calibracao.assinadoEm).toLocaleDateString('pt-BR')}`}
            </span>
          )}
        </div>
        <button onClick={onFechar} className="text-slate-500 hover:text-slate-200 text-xl leading-none transition-colors">×</button>
      </div>

      {/* Corpo: split PDF | Dados */}
      <div className="flex-1 overflow-hidden flex min-h-0">
        {/* Esquerda — PDF */}
        <div className="flex-1 border-r border-[var(--surface-4)] bg-[var(--surface-1)] flex flex-col">
          {arquivoUrl && !pdfError ? (
            <iframe
              src={arquivoUrl}
              title="Certificado"
              className="w-full h-full border-0"
              onError={() => setPdfError(true)}
              onLoad={(e) => {
                try {
                  const body = e.target.contentDocument?.body?.innerText || '';
                  if (body.includes('suspended') || body.includes('not found') || body.includes('404')) setPdfError(true);
                } catch {}
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-3">
              <span className="text-4xl opacity-30">📄</span>
              {pdfError ? (
                <>
                  <p className="text-sm text-amber-400">Certificado indisponível.</p>
                  {arquivoUrl && (
                    <a href={arquivoUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-400 underline">
                      Abrir em nova aba
                    </a>
                  )}
                </>
              ) : (
                <p className="text-sm">Nenhum certificado anexado.</p>
              )}
            </div>
          )}
        </div>

        {/* Direita — Dados do laudo */}
        <div className="w-[420px] flex-shrink-0 bg-[var(--surface-1)]">
          <PainelDadosLaudo calibracao={calibracao} />
        </div>
      </div>

      {/* Rodapé */}
      <div className="px-5 py-3 border-t border-[var(--surface-4)] bg-[var(--surface-2)] flex-shrink-0">
        {readOnly ? (
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-600">Laudo concluído — imutável (ISO 10012 §7.2).</p>
            <div className="flex gap-2">
              {arquivoUrl && (
                <a
                  href={arquivoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 text-xs font-medium bg-slate-700 border border-slate-600 text-slate-200 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  ↗ Abrir PDF
                </a>
              )}
              <button
                onClick={onFechar}
                className="px-4 py-1.5 text-xs font-semibold bg-[var(--surface-4)] hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        ) : !mostrarRejeicao ? (
          <div className="space-y-2">
            {erroAssinar && (
              <div className="bg-red-900/30 border border-red-800/50 rounded-lg px-3 py-2">
                <p className="text-xs text-red-300">{erroAssinar}</p>
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-600">Revise o certificado e os dados antes de assinar.</p>
              <div className="flex gap-2">
                <button
                  onClick={onFechar}
                  className="px-3 py-1.5 text-xs text-slate-400 border border-[var(--surface-4)] rounded-lg hover:text-slate-200 transition-colors"
                >
                  Fechar
                </button>
                {onEditar && (
                  <button
                    onClick={onEditar}
                    className="px-3 py-1.5 text-xs font-medium bg-slate-700 border border-slate-600 text-slate-200 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    ✏️ Editar
                  </button>
                )}
                <button
                  onClick={() => setMostrarRejeicao(true)}
                  className="px-3 py-1.5 text-xs font-medium bg-red-900/50 border border-red-800/50 text-red-300 rounded-lg hover:bg-red-900/80 transition-colors"
                >
                  Rejeitar
                </button>
                <button
                  onClick={() => { setErroAssinar(''); onAssinar(setErroAssinar); }}
                  className="px-4 py-1.5 text-xs font-semibold bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                >
                  ✓ Assinar e Concluir
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-400 font-medium">Motivo da rejeição:</p>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ex: Dados de medição incompletos..."
                autoFocus
                className="flex-1 min-w-[240px] bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-red-500"
              />
              <button
                onClick={() => { setMostrarRejeicao(false); setMotivo(''); }}
                className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => onRejeitar(motivo)}
                disabled={!motivo.trim()}
                className="px-4 py-1.5 text-xs font-semibold bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Modal de importação histórica ───────────────────────────────────────────
function ImportModal({ onFechar, onConcluido }) {
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  async function enviar() {
    if (!arquivo) return;
    setEnviando(true);
    setErro('');
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      const { data } = await api.post('/calibrations/import', fd);
      setResultado(data);
      onConcluido();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao importar.');
    } finally {
      setEnviando(false);
    }
  }

  async function baixarTemplate() {
    try {
      const res = await api.get('/calibrations/template-historico', { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = href; a.download = 'template_historico.xlsx'; a.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      console.error('[baixarTemplate]', err);
      alert('Não foi possível baixar o template. Tente novamente.');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl w-full max-w-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-[var(--surface-4)] flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-white">Importar Calibrações Históricas</h2>
            <p className="text-xs text-slate-500 mt-0.5">Importe registros anteriores via planilha Excel</p>
          </div>
          <button onClick={onFechar} className="text-slate-500 hover:text-white text-xl">×</button>
        </div>

        <div className="p-6 space-y-5">
          {!resultado ? (
            <>
              {/* Colunas esperadas */}
              <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Colunas esperadas na planilha</p>
                <div className="flex flex-wrap gap-1.5">
                  {['TAG*','Data Calibração*','Próxima Calibração*','Tipo','Fornecedor','Nº Certificado','Resultado*','Maior Erro','EMP','Observações'].map(c => (
                    <span key={c} className={`text-[10px] px-2 py-0.5 rounded-full border ${c.endsWith('*') ? 'bg-blue-950/40 border-blue-800/40 text-blue-300' : 'bg-[var(--surface-4)] border-slate-700 text-slate-400'}`}>
                      {c}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600 mt-2">* Obrigatório · Resultado: aprovado | reprovado | aprovado com restricao</p>
              </div>

              {/* Upload */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Arquivo Excel (.xlsx)</label>
                <input type="file" accept=".xlsx,.xls"
                  onChange={e => setArquivo(e.target.files[0])}
                  className="w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-700 file:text-white file:text-xs file:cursor-pointer hover:file:bg-blue-600" />
              </div>

              {erro && (
                <div className="text-red-400 text-sm bg-red-950/30 border border-red-900/30 rounded-lg px-3 py-2">&#9888; {erro}</div>
              )}

              <div className="flex gap-3 justify-end">
                <button onClick={baixarTemplate} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] rounded-lg transition-colors">
                  ↓ Template
                </button>
                <button onClick={onFechar} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancelar</button>
                <button onClick={enviar} disabled={!arquivo || enviando}
                  className="px-5 py-2 bg-emerald-700 hover:bg-emerald-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50">
                  {enviando ? 'Importando...' : '↑ Importar'}
                </button>
              </div>
            </>
          ) : (
            /* Resultado */
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1 bg-emerald-900/30 border border-emerald-800/40 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-300">{resultado.importados}</div>
                  <div className="text-xs text-emerald-500 mt-1">Importados</div>
                </div>
                <div className={`flex-1 rounded-xl p-4 text-center border ${resultado.erros > 0 ? 'bg-red-900/30 border-red-800/40' : 'bg-slate-800/30 border-slate-700/40'}`}>
                  <div className={`text-3xl font-bold ${resultado.erros > 0 ? 'text-red-300' : 'text-slate-500'}`}>{resultado.erros}</div>
                  <div className={`text-xs mt-1 ${resultado.erros > 0 ? 'text-red-500' : 'text-slate-600'}`}>Erros</div>
                </div>
              </div>

              {resultado.detalhes?.erros?.length > 0 && (
                <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Linhas com erro</p>
                  {resultado.detalhes.erros.map((e, i) => (
                    <div key={i} className="text-xs text-red-400">Linha {e.linha}{e.tag ? ` (${e.tag})` : ''}: {e.motivo}</div>
                  ))}
                </div>
              )}

              <button onClick={onFechar}
                className="w-full py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors">
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CalibrationsPage() {
  const { user } = useAuth();
  const { formatarData: formatarVencimento } = useTenant();
  const [calibracoes, setCalibracoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtroCalibrationType, setFiltroCalibrationType] = useState('');
  const [filtroResultado, setFiltroResultado] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [calibracaoEditando, setCalibracaoEditando] = useState(null);
  const [modalRejeicao, setModalRejeicao] = useState(null); // id da calibração
  const [modalVisualizacao, setModalVisualizacao] = useState(null); // objeto calibração
  const [modalDetalhe, setModalDetalhe] = useState(null); // calibração concluída (read-only)
  const [showImport, setShowImport] = useState(false);
  const [showCertImport, setShowCertImport] = useState(false);

  const [toast, setToast] = useState(null);
  const LIMITE = 15;

  const buscarCalibracoes = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params = new URLSearchParams({ pagina, limite: LIMITE });
      if (filtroCalibrationType) params.append('calibrationType', filtroCalibrationType);
      if (filtroResultado) params.append('resultado', filtroResultado);
      const { data } = await api.get(`/calibrations?${params.toString()}`);
      setCalibracoes(data.dados || data);
      setTotal(data.total || 0);
    } catch {
      setErro('Não foi possível carregar as calibrações.');
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtroCalibrationType, filtroResultado]);

  useEffect(() => { buscarCalibracoes(); }, [buscarCalibracoes]);
  useEffect(() => { setPagina(1); }, [filtroCalibrationType, filtroResultado]);

  function exibirToast(mensagem, tipo = 'sucesso') {
    setToast({ mensagem, tipo });
    setTimeout(() => setToast(null), 3500);
  }

  function onSucesso() {
    setModalAberto(false);
    setCalibracaoEditando(null);
    buscarCalibracoes();
    exibirToast(calibracaoEditando ? 'Calibração atualizada.' : 'Calibração registrada.');
  }

  async function enviarRevisao(id) {
    try {
      await api.patch(`/calibrations/${id}/enviar-revisao`);
      buscarCalibracoes();
      exibirToast('Laudo enviado para revisão.');
    } catch (err) {
      exibirToast(err.response?.data?.mensagem || 'Erro ao enviar para revisão.', 'erro');
    }
  }

  async function rejeitar(id, motivo) {
    try {
      await api.patch(`/calibrations/${id}/rejeitar`, { motivo });
      setModalRejeicao(null);
      setModalVisualizacao(null);
      buscarCalibracoes();
      exibirToast('Laudo devolvido para rascunho.');
    } catch (err) {
      exibirToast(err.response?.data?.mensagem || 'Erro ao rejeitar laudo.', 'erro');
    }
  }

  async function assinarViaModal(id, setErroModal) {
    try {
      await api.patch(`/calibrations/${id}/assinar`);
      setModalVisualizacao(null);
      buscarCalibracoes();
      exibirToast('Laudo assinado e concluído.');
    } catch (err) {
      const msg = err.response?.data?.mensagem || 'Erro ao assinar laudo.';
      setErroModal?.(msg);
      exibirToast(msg, 'erro');
    }
  }

  const totalPaginas = Math.ceil(total / LIMITE);
  const role = user?.role;

  return (
    <div className="min-h-screen bg-[var(--surface-1)] text-slate-200 p-6">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-xl text-sm font-medium ${
          toast.tipo === 'sucesso'
            ? 'bg-emerald-800 text-emerald-100 border border-emerald-700'
            : 'bg-red-800 text-red-100 border border-red-700'
        }`}>
          {toast.mensagem}
        </div>
      )}

      {/* Modal de rejeição rápida (sem PDF) */}
      {modalRejeicao && (
        <ModalRejeicao
          onConfirmar={(motivo) => rejeitar(modalRejeicao, motivo)}
          onCancelar={() => setModalRejeicao(null)}
        />
      )}

      {/* Modal de visualização com PDF (revisor) */}
      {modalVisualizacao && (
        <ModalVisualizacao
          calibracao={modalVisualizacao}
          onFechar={() => setModalVisualizacao(null)}
          onAssinar={(setErro) => assinarViaModal(modalVisualizacao._id, setErro)}
          onRejeitar={(motivo) => rejeitar(modalVisualizacao._id, motivo)}
          onEditar={() => { setCalibracaoEditando(modalVisualizacao); setModalVisualizacao(null); setModalAberto(true); }}
        />
      )}

      {/* Modal de detalhe read-only (calibração concluída) */}
      {modalDetalhe && (
        <ModalVisualizacao
          calibracao={modalDetalhe}
          onFechar={() => setModalDetalhe(null)}
          readOnly
        />
      )}

      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Calibrações</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {total > 0 ? `${total} registro${total !== 1 ? 's' : ''} encontrado${total !== 1 ? 's' : ''}` : 'Nenhum registro'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['supervisor', 'operador'].includes(role) && (
            <button
              onClick={() => setShowImport(true)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg transition-colors border border-slate-600"
            >
              ↑ Importar histórico
            </button>
          )}
          {['supervisor', 'operador'].includes(role) && (
            <button
              onClick={() => setShowCertImport(true)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg transition-colors border border-slate-600"
            >
              ↑ Certificados
            </button>
          )}
          {['supervisor', 'operador'].includes(role) && (
            <button
              onClick={() => { setCalibracaoEditando(null); setModalAberto(true); }}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <span className="text-lg leading-none">+</span>
              Nova Calibração
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg overflow-hidden text-sm">
          {[
            { valor: '', rotulo: 'Todos os tipos' },
            { valor: 'externa', rotulo: '🏭 Externa' },
            { valor: 'interna', rotulo: '🔧 Interna' },
          ].map((op) => (
            <button
              key={op.valor}
              onClick={() => setFiltroCalibrationType(op.valor)}
              className={`px-3 py-2 transition-colors ${
                filtroCalibrationType === op.valor ? 'bg-blue-700 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {op.rotulo}
            </button>
          ))}
        </div>

        <select
          value={filtroResultado}
          onChange={(e) => setFiltroResultado(e.target.value)}
          className="bg-[var(--surface-3)] border border-[var(--surface-4)] text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
        >
          <option value="">Todos os resultados</option>
          <option value="aprovado">Aprovado</option>
          <option value="reprovado">Reprovado</option>
          <option value="aprovado_com_restricao">Aprovado com Restrição</option>
        </select>
      </div>

      {erro && (
        <div className="bg-red-900/40 border border-red-700 text-red-300 rounded-lg px-4 py-3 mb-4 text-sm">
          {erro}
        </div>
      )}

      {/* Tabela */}
      <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
        {carregando ? (
          <div className="space-y-px">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 bg-[var(--surface-4)]/50 animate-pulse" />
            ))}
          </div>
        ) : calibracoes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <span className="text-4xl mb-3">📋</span>
            <p className="text-sm">Nenhuma calibração encontrada.</p>
            {['supervisor', 'operador'].includes(role) && (
              <button
                onClick={() => { setCalibracaoEditando(null); setModalAberto(true); }}
                className="mt-4 text-blue-400 hover:text-blue-300 text-sm underline underline-offset-2"
              >
                Registrar primeira calibração
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--surface-4)] text-xs text-slate-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-medium">Instrumento</th>
                  <th className="text-left px-4 py-3 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Próxima</th>
                  <th className="text-left px-4 py-3 font-medium">Resultado</th>
                  <th className="text-left px-4 py-3 font-medium">Fornecedor</th>
                  <th className="text-left px-4 py-3 font-medium">Laudo</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {calibracoes.map((cal) => {
                  const badgeTipo = BADGE_TIPO[cal.calibrationType] || BADGE_TIPO.externa;
                  const badgeRes = BADGE_RESULTADO[cal.resultado] || {};
                  const badgeLaudo = BADGE_LAUDO[cal.statusLaudo] || {};

                  return (
                    <tr
                      key={cal._id}
                      className="border-b border-[var(--surface-4)]/60 last:border-0 hover:bg-[var(--surface-4)]/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-200 font-mono text-xs bg-[var(--surface-4)] px-2 py-0.5 rounded">
                          {cal.instrument?.tag || '—'}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badgeTipo.classe}`}>
                          {cal.calibrationType === 'externa' ? '🏭' : '🔧'}
                          {badgeTipo.rotulo}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                        {formatarData(cal.data)}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={proximaDataClasse(cal.proximaData)}>
                          {formatarVencimento(cal.proximaData)}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        {cal.resultado && (
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${badgeRes.classe}`}>
                            {badgeRes.rotulo}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-slate-400 text-xs max-w-[160px] truncate">
                        {cal.calibrationType === 'interna'
                          ? <span className="text-slate-600 italic">Interno</span>
                          : cal.fornecedor || <span className="text-slate-600">—</span>}
                      </td>

                      {/* Status do laudo */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs w-fit ${badgeLaudo.classe}`}>
                            {badgeLaudo.rotulo}
                          </span>
                          {cal.statusLaudo === 'rascunho' && cal.motivoRejeicao && (
                            <span className="text-xs text-red-400" title={cal.motivoRejeicao}>
                              Rejeitado
                            </span>
                          )}
                          {cal.statusLaudo === 'concluido' && cal.assinadoPor && (
                            <span className="text-xs text-slate-500">
                              por {cal.assinadoPor}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Ações */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Ver detalhe — todos os roles, laudo concluído */}
                          {cal.statusLaudo === 'concluido' && (
                            <button
                              onClick={() => setModalDetalhe(cal)}
                              title="Ver detalhes do laudo"
                              className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-colors"
                            >
                              Ver
                            </button>
                          )}

                          {/* Finalizar rascunho — supervisor/operador */}
                          {['supervisor', 'operador'].includes(role) && cal.statusLaudo === 'rascunho' && (
                            <button
                              onClick={() => enviarRevisao(cal._id)}
                              title="Finalizar e enviar para revisão"
                              className="text-xs px-2 py-1 rounded bg-amber-900/40 text-amber-300 border border-amber-800/50 hover:bg-amber-900/70 transition-colors"
                            >
                              Finalizar
                            </button>
                          )}

                          {/* Revisar (PDF + aprovar/rejeitar/editar) — revisor/supervisor */}
                          {['revisor', 'supervisor'].includes(role) && cal.statusLaudo === 'em_revisao' && (
                            <button
                              onClick={() => setModalVisualizacao(cal)}
                              title="Revisar certificado e aprovar ou rejeitar"
                              className="text-xs px-2 py-1 rounded bg-indigo-900/40 text-indigo-300 border border-indigo-800/50 hover:bg-indigo-900/70 transition-colors"
                            >
                              Revisar
                            </button>
                          )}

                          {/* Editar — supervisor/operador, laudo não concluído */}
                          {['supervisor', 'operador'].includes(role) && cal.statusLaudo !== 'concluido' && (
                            <button
                              onClick={() => { setCalibracaoEditando(cal); setModalAberto(true); }}
                              className="text-slate-500 hover:text-blue-400 transition-colors"
                              title="Editar calibração"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-400">
          <span>Página {pagina} de {totalPaginas}</span>
          <div className="flex gap-2">
            <button
              disabled={pagina === 1}
              onClick={() => setPagina((p) => p - 1)}
              className="px-3 py-1.5 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ← Anterior
            </button>
            <button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina((p) => p + 1)}
              className="px-3 py-1.5 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Próxima →
            </button>
          </div>
        </div>
      )}

      {/* Modal de criação / edição */}
      {modalAberto && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="w-full max-w-3xl bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--surface-4)]">
              <h2 className="text-base font-semibold text-slate-100">
                {calibracaoEditando ? 'Editar Calibração' : 'Nova Calibração'} <span className="text-[10px] font-normal text-yellow-400">v21</span>
              </h2>
              <button
                onClick={() => { setModalAberto(false); setCalibracaoEditando(null); }}
                className="text-slate-500 hover:text-slate-200 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-5">
              <CalibrationForm
                calibracaoExistente={calibracaoEditando}
                onSucesso={onSucesso}
                onCancelar={() => { setModalAberto(false); setCalibracaoEditando(null); }}
                onRascunhoSalvo={buscarCalibracoes}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de importação histórica */}
      {showImport && (
        <ImportModal
          onFechar={() => setShowImport(false)}
          onConcluido={() => { buscarCalibracoes(); }}
        />
      )}

      {/* Modal de import de certificados via IA */}
      {showCertImport && (
        <CertificateImportModal
          isOpen={showCertImport}
          onClose={() => setShowCertImport(false)}
          onImportComplete={() => buscarCalibracoes()}
        />
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatarData(dataStr) {
  if (!dataStr) return '—';
  return new Date(dataStr).toLocaleDateString('pt-BR');
}

function proximaDataClasse(dataStr) {
  if (!dataStr) return 'text-slate-400';
  const diff = new Date(dataStr) - new Date();
  const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
  if (dias < 0) return 'text-red-400 font-medium';
  if (dias <= 30) return 'text-amber-400 font-medium';
  return 'text-slate-300';
}
